/**
 * Filter ActionDefs by relevance to the user's query.
 *
 * Gap 3 from the harness engineering analysis: instead of dumping
 * all 129 actions into the LLM prompt ("dump and pray"), we score
 * each action by semantic similarity to the user's message and only
 * include the most relevant ones plus a set of universal anchors.
 *
 * v1: TF-IDF embedding-based scoring with domain synonyms, stemming,
 * and cosine similarity. Replaces v0 keyword substring matching.
 *
 * @module gibbs/text-to-action/filterActions
 */

import { getAllActions, checkPreconditions } from '../registry/index';
import {
    initEmbeddings, rankActions, isInitialized,
} from './actionEmbeddings';

// ─── Configuration ─────────────────────────────────────────────────────────

/**
 * Actions always included regardless of relevance score.
 * These are universally useful operations that the LLM should always
 * be able to pick from.
 *
 * Exported so the action-reference audit test can verify every
 * anchor still resolves in the registry.
 */
export const ANCHOR_ACTION_IDS = new Set([
    // Target setup
    'load-protein-from-pdb',
    'load-protein-from-file',
    'fold-protein',
    // Compound management
    'load-compound',
    'load-starter-compounds',
    'remove-compound',
    // Core computation
    'energy-minimize',
    'get-energies',
    'dock-compound',
    'predict-gnina-scores',
    // Display essentials
    'set-view',
    'set-display-style',
    'show-hydrogen-bonds',
    'open-compound-table',
    // Workspace
    'undo',
    'redo',
    'zap-all',
    'restore-state',
]);

/**
 * Budget: how many actions to include per tier.
 * Smaller = faster, cheaper, less hallucination.
 * Larger = more coverage for edge-case requests.
 */
const TIER_BUDGETS = {
    command: 25,
    workflow: 40,
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Filter actions for the command tier, ranked by relevance to the
 * user's message. Includes anchor actions + top-scored actions up
 * to the tier budget.
 *
 * @param {Object} workspaceState - Workspace state for precondition checking
 * @param {string} [userMessage=''] - The user's input for relevance scoring
 * @returns {import('../registry/schema.js').ActionDef[]}
 */
export function filterForCommandTier(workspaceState, userMessage) {
    return filterByRelevance(userMessage, 'command', workspaceState);
}

/**
 * Filter actions for the workflow tier, ranked by relevance.
 * Larger budget than command tier but still not dumping all 129.
 *
 * @param {string} [userMessage=''] - The user's input for relevance scoring
 * @returns {import('../registry/schema.js').ActionDef[]}
 */
export function filterForWorkflowTier(userMessage) {
    return filterByRelevance(userMessage, 'workflow', null);
}

/**
 * Core filtering logic used by both tiers.
 *
 * @param {string} userMessage
 * @param {'command' | 'workflow'} tier
 * @param {Object|null} workspaceState
 * @returns {import('../registry/schema.js').ActionDef[]}
 */
function filterByRelevance(userMessage, tier, workspaceState) {
    const all = getAllActions().filter(
        (a) => a.status === 'implemented' || a.status === 'planned'
    );
    const budget = TIER_BUDGETS[tier] || TIER_BUDGETS.command;

    // Explicit-actionId promotion: if the user typed any registered
    // actionId verbatim (e.g., "Apply replace-with-bioisostere to cmpd_1"),
    // guarantee that action survives the budget cut. Without this, TF-IDF
    // ranking can drop the explicitly-named action below similarly-worded
    // alternatives, and the LLM substitutes a wrong one.
    //
    // Surfaced during the 2026-05-14 Bill Kinney walkthrough — the LLM
    // remapped `replace-with-bioisostere` to `replace-with-fragment-set`
    // because the former wasn't in the filtered set the LLM saw.
    const lowerMessage = (userMessage || '').toLowerCase();
    const explicitlyNamed = new Set(
        all
            .map((a) => a.id)
            .filter((id) => lowerMessage.includes(id))
    );

    // Lazy-init embeddings on first call
    if (!isInitialized()) {
        try {
            initEmbeddings(all);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[Gibbs] Embedding init failed, using all actions:', err);
            return all.slice(0, budget);
        }
    }

    // Rank by semantic similarity
    const ranked = rankActions(userMessage || '', all);

    // Annotate with anchor status and precondition state
    const scored = ranked.map(({ action, score }) => ({
        action,
        score,
        isAnchor: ANCHOR_ACTION_IDS.has(action.id),
        isExplicitlyNamed: explicitlyNamed.has(action.id),
        preconditionsMet: workspaceState
            ? checkPreconditions(action.id, workspaceState).met
            : true,
    }));

    // Partition into anchors, explicitly-named, and non-anchors
    const anchors = scored.filter((s) => s.isAnchor);
    const explicit = scored.filter((s) => s.isExplicitlyNamed && !s.isAnchor);
    const nonAnchors = scored.filter((s) => !s.isAnchor && !s.isExplicitlyNamed);

    // Non-anchors already sorted by score (from rankActions).
    // Tie-break by precondition status.
    nonAnchors.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if (a.preconditionsMet !== b.preconditionsMet) {
            return a.preconditionsMet ? -1 : 1;
        }
        return 0;
    });

    // Take anchors + explicitly-named + top non-anchors up to budget.
    // Explicitly-named actions get included regardless of the budget cut.
    const reserved = anchors.length + explicit.length;
    const remaining = budget - reserved;
    const topNonAnchors = nonAnchors.slice(0, Math.max(0, remaining));

    // Merge and deduplicate
    const resultMap = new Map();
    for (const s of anchors) resultMap.set(s.action.id, s.action);
    for (const s of explicit) resultMap.set(s.action.id, s.action);
    for (const s of topNonAnchors) resultMap.set(s.action.id, s.action);

    return [...resultMap.values()];
}
